import { useEffect } from "react";

import {
  DEFAULT_MAIN_VIEW_REVEAL_BOTTOM_RESERVE,
  DEFAULT_MAIN_VIEW_REVEAL_TOP_RESERVE,
} from "../../../platform/mainViewScrollReveal";

const DEFAULT_STABLE_FRAME_LIMIT = 12;

export function waitForNextAnimationFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function isUsableRect(rect) {
  return (
    Number.isFinite(rect?.width) &&
    rect.width > 0 &&
    Number.isFinite(rect?.height) &&
    rect.height > 0
  );
}

function areRectsEquivalent(previousRect, nextRect) {
  if (!previousRect || !nextRect) {
    return false;
  }

  return (
    Math.abs(Number(previousRect.top) - Number(nextRect.top)) < 1 &&
    Math.abs(Number(previousRect.bottom) - Number(nextRect.bottom)) < 1 &&
    Math.abs(Number(previousRect.height) - Number(nextRect.height)) < 1 &&
    Math.abs(Number(previousRect.width) - Number(nextRect.width)) < 1
  );
}

export async function waitForStablePanelRoot({
  panelRootRef,
  isCancelled = () => false,
  maxFrames = DEFAULT_STABLE_FRAME_LIMIT,
}) {
  let previousRect = null;
  let stableFrameCount = 0;
  let lastMeasuredNode = null;

  for (let frameIndex = 0; frameIndex < maxFrames; frameIndex += 1) {
    if (isCancelled()) {
      return null;
    }

    await waitForNextAnimationFrame();

    const nextNode = panelRootRef?.current;
    const nextRect = nextNode?.getBoundingClientRect?.();
    if (!nextNode || !isUsableRect(nextRect)) {
      stableFrameCount = 0;
      previousRect = null;
      continue;
    }

    lastMeasuredNode = nextNode;
    if (previousRect && areRectsEquivalent(previousRect, nextRect)) {
      stableFrameCount += 1;
    } else {
      stableFrameCount = 0;
    }

    previousRect = nextRect;

    if (stableFrameCount >= 1) {
      return nextNode;
    }
  }

  return lastMeasuredNode;
}

export async function revealFoodPickerInMainView({
  panelRootRef,
  revealInMainView,
  focusSearchInput,
  topReserve = DEFAULT_MAIN_VIEW_REVEAL_TOP_RESERVE,
  bottomReserve = DEFAULT_MAIN_VIEW_REVEAL_BOTTOM_RESERVE,
  isCancelled = () => false,
}) {
  if (
    typeof requestAnimationFrame !== "function" ||
    typeof revealInMainView !== "function"
  ) {
    return false;
  }

  const panelNode = await waitForStablePanelRoot({
    panelRootRef,
    isCancelled,
  });
  if (!panelNode || isCancelled()) {
    return false;
  }

  revealInMainView(panelNode, {
    topReserve,
    bottomReserve,
    animated: true,
  });

  const settledAfterInitialReveal = await waitForStablePanelRoot({
    panelRootRef,
    isCancelled,
  });
  if (!settledAfterInitialReveal || isCancelled()) {
    return false;
  }

  if (isCancelled()) {
    return false;
  }

  focusSearchInput?.({
    selectText: true,
    ignorePressGuard: true,
  });

  const settledAfterFocus = await waitForStablePanelRoot({
    panelRootRef,
    isCancelled,
  });
  if (!settledAfterFocus || isCancelled()) {
    return false;
  }

  if (isCancelled()) {
    return false;
  }

  revealInMainView(panelNode, {
    topReserve,
    bottomReserve,
    animated: true,
  });

  return true;
}

export function useFoodPickerRevealInMainView({
  isVisible,
  panelRootRef,
  revealInMainView,
  focusSearchInput,
  topReserve = DEFAULT_MAIN_VIEW_REVEAL_TOP_RESERVE,
  bottomReserve = DEFAULT_MAIN_VIEW_REVEAL_BOTTOM_RESERVE,
}) {
  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    let cancelled = false;

    revealFoodPickerInMainView({
      panelRootRef,
      revealInMainView,
      focusSearchInput,
      topReserve,
      bottomReserve,
      isCancelled: () => cancelled,
    }).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    bottomReserve,
    focusSearchInput,
    isVisible,
    panelRootRef,
    revealInMainView,
    topReserve,
  ]);
}
